import React, { useState } from "react";
import { css } from "@emotion/css";
import { useNavigate, useLocation } from "react-router-dom";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import { newLogo } from "../assets/images";
import { getUserKey, deleteUserKey } from "../utils/auth";
import { config } from "../config";
import { toast } from "react-toastify";

const { FiMail, FiPhone, FiMenu, FiX } = FiIcons;

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const user = getUserKey();

    const isDashboard = location.pathname === "/dashboard";

    const headerStyles = {
        header: css`
            background-color: white;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        `,
        container: css`
            margin: 0;
            padding: 0 1rem;
            @media (min-width: 640px) {
                padding: 0 1.5rem;
            }
            @media (min-width: 1024px) {
                padding: ${isDashboard ? "0 2rem" : "1rem 2rem"};
            }
        `,
        headerContent: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: ${isDashboard ? "0" : "0.5rem 0"};
            height: ${isDashboard ? "75px" : "auto"};
            @media (max-width: 1040px) {
                padding: ${isDashboard ? "0" : "1rem 0"};
            }
        `,
        logoSection: css`
            display: flex;
            align-items: center;
            gap: 0.75rem;
            cursor: pointer;
        `,
        logo: css`
            width: ${isDashboard ? "50px" : "7rem"};
            height: ${isDashboard ? "50px" : "7rem"};
            border-radius: 9999px;
            object-fit: cover;
            
            @media (max-width: 768px) {
                width: ${isDashboard ? "40px" : "3.5rem"};
                height: ${isDashboard ? "40px" : "3.5rem"};
            }
        `,
        title: css`
            font-size: ${isDashboard ? "24px" : "35px"};
            font-weight: 900;
            color: #f5b940;
            font-family: "Akagi Pro", sans-serif;

            @media (max-width: 768px) {
                font-size: ${isDashboard ? "18px" : "24px"};
            }
        `,
        desktopMenu: css`
            display: none;
            align-items: center;
            gap: 1.5rem;
            @media (min-width: 768px) {
                display: flex;
                gap: 1rem;
            }
            @media (min-width: 1024px) {
                gap: 1.5rem;
            }
        `,
        contactItem: css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #4b5563;
            border: 1px solid #f0f0f0;
            padding: 7px;
            border-radius: 15px;
            white-space: nowrap;
        `,
        contactText: css`
            font-size: 0.875rem;
        `,
        loginButton: css`
            background-color: #fc3c3c;
            color: white;
            font-weight: 600;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            cursor: pointer;
            border: none;
            flex-shrink: 0;
            transition: background-color 0.2s;
            &:hover {
                background-color: #b91c1c;
            }
        `,
        dashboardButton: css`
            background-color: #2563eb;
            color: white;
            font-weight: 600;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            cursor: pointer;
            border: none;
            flex-shrink: 0;
            transition: background-color 0.2s;
            &:hover {
                background-color: #1d4ed8;
            }
        `,
        registerButton: css`
            background-color: #2563eb;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            transition: background-color 0.2s;
            &:hover {
                background-color: #1d4ed8;
            }
        `,
        mobileMenuButton: css`
            display: block;
            @media (min-width: 768px) {
                display: none;
            }
        `,
        mobileMenu: css`
            display: block;
            padding: 1rem 0;
            border-top: 1px solid #e5e7eb;
            @media (min-width: 768px) {
                display: none;
            }
        `,
        mobileMenuContent: css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
        `,
        mobileContactItem: css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #4b5563;
        `,
        mobileLoginButton: css`
            width: 100%;
            background-color: #fc3c3c;
            color: white;
            font-weight: 600;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            cursor: pointer;
            border: none;
            transition: background-color 0.2s;
            &:hover {
                background-color: #b91c1c;
            }
        `,
        mobileDashboardButton: css`
            width: 100%;
            background-color: #2563eb;
            color: white;
            font-weight: 600;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            cursor: pointer;
            border: none;
            transition: background-color 0.2s;
            &:hover {
                background-color: #1d4ed8;
            }
        `,
        mobileRegisterButton: css`
            width: 100%;
            background-color: #2563eb;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.5rem;
            transition: background-color 0.2s;
            &:hover {
                background-color: #1d4ed8;
            }
        `,
        icon: css`
            width: 1rem;
            height: 1rem;
        `,
        menuIcon: css`
            background: white;
            width: 1.5rem;
            height: 1.5rem;
        `,
    };

    const handleMenuToggle = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleMenuToggle();
        }
    };

    const handleLogout = async () => {
        try {
            if (user && user.token) {
                const response = await fetch(`${config.serverUrl}/api/auth/logout`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`
                    }
                });

                const result = await response.json().catch(() => ({}));

                if (response.ok && result.status === "success") {
                    deleteUserKey();
                    toast.success(result.message || "Logged out successfully!");
                    navigate("/login");
                } else if (response.status === 401 || result?.msg?.toLowerCase?.().includes("session expired")) {
                    // Session already expired - still clear local state and redirect
                    deleteUserKey();
                    toast.info("Session expired. You have been signed out.");
                    navigate("/login");
                } else {
                    deleteUserKey();
                    navigate("/login");
                    toast.warning(result.message || result.msg || "Session ended.");
                }
            } else {
                // If there's no valid token but we attempt to logout anyway, just clear and redirect
                deleteUserKey();
                navigate("/login");
            }
        } catch (err) {
            console.error("Logout API failed", err);
            // Network error etc - still clear local session so user can retry
            deleteUserKey();
            navigate("/login");
            toast.info("You have been signed out.");
        }
    };

    const handleLogoClick = () => {
        navigate("/");
    };

    return (
        <header className={headerStyles.header} role="banner">
            <div className={headerStyles.container}>
                <div className={headerStyles.headerContent}>
                    <div className={headerStyles.logoSection} onClick={handleLogoClick}>
                        <img
                            src={newLogo}
                            alt="Club Mango Logo - A distinctive logo representing the Club Mango community"
                            className={headerStyles.logo}
                        />
                        <h1 className={headerStyles.title}>
                            Club Mango Friends
                        </h1>
                    </div>

                    <nav
                        className={headerStyles.desktopMenu}
                        role="navigation"
                        aria-label="Main navigation"
                    >
                        <div
                            className={headerStyles.contactItem}
                            role="group"
                            aria-label="Contact information"
                        >
                            <SafeIcon
                                icon={FiMail}
                                className={headerStyles.icon}
                                aria-hidden="true"
                            />
                            <span className={headerStyles.contactText}>
                                <a
                                    href="mailto:clubmangofriends@divineharmony.co.in"
                                    aria-label="Send email to clubmangofriends@divineharmony.co.in"
                                >
                                    clubmangofriends@divineharmony.co.in
                                </a>
                            </span>
                        </div>
                        <div
                            className={headerStyles.contactItem}
                            role="group"
                            aria-label="Phone contact"
                        >
                            <SafeIcon
                                icon={FiPhone}
                                className={headerStyles.icon}
                                aria-hidden="true"
                            />
                            <span className={headerStyles.contactText}>
                                <a
                                    href="tel:+919503692077"
                                    aria-label="Call +91 95036 92077"
                                >
                                    +919503692077
                                </a>
                            </span>
                        </div>
                        {/* Auth Buttons Desktop */}
                        {isDashboard ? (
                            <button
                                className={headerStyles.loginButton}
                                onClick={handleLogout}
                                aria-label="Logout"
                            >
                                Logout
                            </button>
                        ) : user ? (
                            <button
                                className={headerStyles.dashboardButton}
                                onClick={() => navigate("/dashboard")}
                                aria-label="Navigate to Dashboard"
                            >
                                Dashboard
                            </button>
                        ) : (
                            <button
                                className={headerStyles.loginButton}
                                onClick={() => navigate("/login")}
                                aria-label="Navigate to Login Page"
                            >
                                Login
                            </button>
                        )}
                    </nav>

                    <button
                        className={headerStyles.mobileMenuButton}
                        onClick={handleMenuToggle}
                        onKeyDown={handleKeyDown}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-menu"
                        aria-label={
                            isMenuOpen
                                ? "Close navigation menu"
                                : "Open navigation menu"
                        }
                        aria-haspopup="true"
                    >
                        <SafeIcon
                            icon={isMenuOpen ? FiX : FiMenu}
                            className={headerStyles.menuIcon}
                            aria-hidden="true"
                        />
                    </button>
                </div>

                {isMenuOpen && (
                    <nav
                        id="mobile-menu"
                        className={headerStyles.mobileMenu}
                        role="navigation"
                        aria-label="Mobile navigation"
                        aria-hidden={!isMenuOpen}
                    >
                        <div className={headerStyles.mobileMenuContent}>
                            <div
                                className={headerStyles.mobileContactItem}
                                role="group"
                                aria-label="Email contact"
                            >
                                <SafeIcon
                                    icon={FiMail}
                                    className={headerStyles.icon}
                                    aria-hidden="true"
                                />
                                <span className={headerStyles.contactText}>
                                    <a
                                        href="mailto:clubmangofriends@divineharmony.co.in"
                                        aria-label="Send email to clubmangofriends@divineharmony.co.in"
                                    >
                                        clubmangofriends@divineharmony.co.in
                                    </a>
                                </span>
                            </div>
                            <div
                                className={headerStyles.mobileContactItem}
                                role="group"
                                aria-label="Phone contact"
                            >
                                <SafeIcon
                                    icon={FiPhone}
                                    className={headerStyles.icon}
                                    aria-hidden="true"
                                />
                                <span className={headerStyles.contactText}>
                                    <a
                                        href="tel:+919503692077"
                                        aria-label="Call +91 95036 92077"
                                    >
                                        +919503692077
                                    </a>
                                </span>
                            </div>
                            {/* Auth Buttons Mobile */}
                            {isDashboard ? (
                                <button
                                    className={headerStyles.mobileLoginButton}
                                    onClick={handleLogout}
                                    aria-label="Logout"
                                >
                                    Logout
                                </button>
                            ) : user ? (
                                <button
                                    className={headerStyles.mobileDashboardButton}
                                    onClick={() => navigate("/dashboard")}
                                    aria-label="Navigate to Dashboard"
                                >
                                    Dashboard
                                </button>
                            ) : (
                                <button
                                    className={headerStyles.mobileLoginButton}
                                    onClick={() => navigate("/login")}
                                    aria-label="Navigate to Login Page"
                                >
                                    Login
                                </button>
                            )}
                        </div>
                    </nav>
                )}
            </div>
        </header>
    );
};

export default Header;
